export type AuthenticationScope = "church" | "platform";

const CHURCH_TOKEN_STORAGE_KEY = "access_token";
const PLATFORM_TOKEN_STORAGE_KEY = "platform_access_token";

const SESSION_MESSAGE_STORAGE_KEY =
  "ministryiq_session_message";

const SESSION_DESTINATION_STORAGE_KEY =
  "ministryiq_session_destination";

export const SESSION_EXPIRED_MESSAGE =
  "Your session has expired. Please sign in again.";

// Tokens may be identical for logins in the same second. A generation also
// invalidates requests across logout/login without relying on token uniqueness.
const generations: Record<AuthenticationScope, number> = { church: 0, platform: 0 };
export function sessionSnapshot(scope: AuthenticationScope) {
  return { token: getSessionToken(scope), generation: generations[scope] };
}
export function isCurrentSession(scope: AuthenticationScope, snapshot: ReturnType<typeof sessionSnapshot>) {
  return snapshot.token === getSessionToken(scope) && snapshot.generation === generations[scope];
}
export function storeSessionToken(scope: AuthenticationScope, token: string) {
  generations[scope]++;
  localStorage.setItem(getTokenStorageKey(scope), token);
}
export function clearSessionToken(scope: AuthenticationScope) {
  generations[scope]++;
  localStorage.removeItem(getTokenStorageKey(scope));
}

const listeners = new Set<(scope: AuthenticationScope) => void>();

export function onSessionExpired(listener: (scope: AuthenticationScope) => void) {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

export class InvalidSessionError extends Error {
  readonly status = 401;
  constructor() {
    super(SESSION_EXPIRED_MESSAGE);
    this.name = "InvalidSessionError";
  }
}

export function getSessionToken(scope: AuthenticationScope) {
  return localStorage.getItem(getTokenStorageKey(scope));
}

function getLoginPath(scope: AuthenticationScope) {
  return scope === "platform"
    ? "/login"
    : "/login";
}

function getTokenStorageKey(scope: AuthenticationScope) {
  return scope === "platform"
    ? PLATFORM_TOKEN_STORAGE_KEY
    : CHURCH_TOKEN_STORAGE_KEY;
}

function isAlreadyOnLoginPage(scope: AuthenticationScope) {
  return window.location.pathname === getLoginPath(scope);
}

export function expireSession(
  scope: AuthenticationScope,
  rejectedToken: string,
) {
  // A late response must not invalidate a newer login or repeat a transition.
  if (!rejectedToken || getSessionToken(scope) !== rejectedToken) return;
  clearSessionToken(scope);

  for (const listener of listeners) listener(scope);

  if (isAlreadyOnLoginPage(scope)) {
    return;
  }

  const currentDestination = [
    window.location.pathname,
    window.location.search,
    window.location.hash,
  ].join("");

  sessionStorage.setItem(
    SESSION_MESSAGE_STORAGE_KEY,
    SESSION_EXPIRED_MESSAGE,
  );

  sessionStorage.setItem(
    SESSION_DESTINATION_STORAGE_KEY,
    currentDestination,
  );

  window.location.replace(getLoginPath(scope));
}

export function consumeSessionMessage() {
  const message = sessionStorage.getItem(
    SESSION_MESSAGE_STORAGE_KEY,
  );

  sessionStorage.removeItem(
    SESSION_MESSAGE_STORAGE_KEY,
  );

  return message;
}

export function consumeSessionDestination(
  fallbackDestination: string,
) {
  const destination = sessionStorage.getItem(
    SESSION_DESTINATION_STORAGE_KEY,
  );

  sessionStorage.removeItem(
    SESSION_DESTINATION_STORAGE_KEY,
  );

  if (!destination) {
    return fallbackDestination;
  }

  const isSafeLocalDestination =
    destination.startsWith("/") &&
    !destination.startsWith("//") &&
    !destination.includes("\\") &&
    !Array.from(destination).some(char => char.charCodeAt(0) <= 32) &&
    !/^\/login(?:[/?#]|$)/.test(destination);

  return isSafeLocalDestination
    ? destination
    : fallbackDestination;
}

